import {
	put,
	take,
	call,
	fork,
	select,
	takeEvery,
	all,
	takeLatest,
	ActionPattern,
	takeLeading,
} from "redux-saga/effects";

import * as actions from "./actionTypes";

import {
	sendPatches,
	jsonPatchSuccess,
	statusReady,
	mergeUpdate,
	statusError,
	statusLoading,
	statusClosed,
} from "data/form/formActions";

import { isReady, getPatches, getStatus } from "./formSelectors";
import { Channel, channel } from "redux-saga";
import {
	authenticated,
	setAuthType,
	appReady,
	createAlertAction,
} from "data/app/appActions";

import { INIT_APP, LOGOUT, AUTHENTICATED } from "../app/actionTypes";
import forms from "lib/api/services/forms";
import tokens from "lib/api/services/tokens";
import {
	Actions,
	PatchConfig,
	SaveFormAction,
	UpdateFormStatus,
} from "./types";
import { statusUpdating } from "./formActions";
import tenants from "lib/api/services/tenants";
import history from "browserHistory";
import { AxiosError, AxiosResponse } from "axios";
import { getErrorMessageOrDefault } from "lib/api/errors";
import normaliseForm from "lib/normalisers/form";
import { FormStatusTypes } from "records/Form";
import { FormConfig } from "../../records/Form";
import { createAlert } from "records/Alert";
import i18n from "i18n";
import workflow from "lib/api/services/workflow";

/**
 * Implements `takeLatest` handling per unique value returned by `scopeSelector`.
 */
export function takeLatestPerScope(
	scopeSelector: (action: Actions) => string,
	pattern: ActionPattern<Actions>,
	worker: (action: Actions) => void
) {
	return fork(scopedFork, scopeSelector, pattern, worker);
}

export function* scopedFork(
	scopeSelector: (action: Actions) => string,
	pattern: ActionPattern<Actions>,
	worker: (action: Actions) => void
) {
	const channels: { [scope: string]: Channel<Actions> } = {};

	yield takeEvery(pattern, function* (action: Actions) {
		const scope = scopeSelector(action);
		if (!channels[scope]) {
			channels[scope] = channel();
			yield takeLatest(channels[scope], worker);
		}
		yield put(channels[scope], action);
	});
}

export function formScope(action: Actions) {
	return action.fid;
}

/**
 * Monitors forms for changes that are ready to sync
 * to the API
 */
export function* watchFormChanges() {
	yield all([
		takeLatestPerScope(formScope, [actions.FORM_GET], getLatest),
		takeLatestPerScope(formScope, [actions.JSON_PATCH], queuePatches),
		takeLatestPerScope(formScope, [actions.SEND_PATCHES], jsonPatch),
		takeLatestPerScope(formScope, [actions.FORM_SAVE], save),
		takeLatestPerScope(formScope, [actions.FORM_SAVE_CLOSE], saveAndClose),
		takeLeading([actions.FORM_CLOSE], close),
	]);
}

export function* getLatest(action: { fid: string }) {
	// Wait for the form to be ready
	yield whenReady(action.fid);

	// Mark the form as updating
	yield put(statusUpdating(action.fid));

	try {
		// Get the latest data for the form
		const response: FormConfig = yield call(forms.get, action.fid);

		// Merge in the latest data
		yield put(mergeUpdate(action.fid, response));
	} catch (e) {
		console.error("Error getting the form instance", e);
	} finally {
		// Mark the form as ready again
		yield put(statusReady(action.fid));
	}
}

export function* whenReady(fid: string) {
	const ready: boolean = yield select(isReady, fid);
	while (!ready) {
		const action: UpdateFormStatus = yield take(actions.FORM_STATUS_UPDATE);
		if (action.fid === fid && action.status === "READY") break;
	}
}

export function* queuePatches(action: Actions) {
	yield whenReady(action.fid);
	yield put(sendPatches(action.fid));
}

export function* jsonPatch(action: Actions) {
	// Get the list of JSON patches
	const patches: PatchConfig[] = yield select(getPatches, action.fid);

	try {
		// group payload by view id and row index
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const payload = patches.reduce((acc: any[], p) => {
			if (
				acc.length === 0 ||
				acc[acc.length - 1].id !== p.vid ||
				acc[acc.length - 1].index !== p.rowIndex
			) {
				acc.push({ id: p.vid, index: p.rowIndex, data: [] });
			}

			acc[acc.length - 1].data.push({
				op: p.op,
				uuid: p.uuid,
				path: p.path,
				...(p.op !== "remove" && { value: p.data.value }),
			});

			return acc;
		}, []);

		const response: AxiosResponse = yield call(
			forms.patch,
			action.fid,
			payload,
			{ handleErrors: true }
		);

		// Remove patches on success
		yield put(
			jsonPatchSuccess(
				action.fid,
				normaliseForm(response.data),
				patches.length
			)
		);

		yield put(statusReady(action.fid));
	} catch (error) {
		console.error(error);
		const e = error as AxiosError;
		if (e.response) {
			const response = e.response as AxiosResponse;

			// 400 errors = expected validation errors
			// Errors should be in the form instance so we can
			// just merge the update in as normal
			if (response.status === 400) {
				yield put(
					jsonPatchSuccess(
						action.fid,
						normaliseForm(response.data),
						patches.length
					)
				);

				yield put(statusReady(action.fid));
			} else {
				yield put(statusError(action.fid));
			}
		}
	}
}

export function* save(action: Actions) {
	yield whenReady(action.fid);
	yield put(statusUpdating(action.fid));

	try {
		const saveAction = action as SaveFormAction;

		// Check if tid is passed so we will save the task rather than the form
		const saveFunc = saveAction.tid ? workflow.saveTask : forms.save;
		const id = saveAction.tid ? saveAction.tid : action.fid;

		const response: AxiosResponse = yield call(saveFunc, id, {
			handleErrors: true,
		});
		// Handles new taskName data from flowable, without sending to form response
		const responseData = {
			...response.data,
		};
		let newTaskData: { taskName?: string } = {};
		if (
			responseData.taskData !== undefined &&
			responseData.taskData !== null
		) {
			newTaskData = responseData.taskData;
			delete responseData.taskData;
		}
		yield put(mergeUpdate(action.fid, normaliseForm(responseData)));
		if (saveAction.updateTaskName && newTaskData.taskName) {
			yield saveAction.updateTaskName(newTaskData.taskName);
		}
		yield put(statusReady(action.fid));
		yield put(
			createAlertAction(
				createAlert({
					title: i18n.t("alerts.recordSaved.title"),
					status: "success",
					timeout: 5000,
				})
			)
		);
	} catch (e) {
		const error = e as AxiosError;

		// 400 errors = expected validation errors
		// Errors should be in the form instance so we can
		// just merge the update in as normal
		if (error.response?.status === 400) {
			yield put(
				mergeUpdate(action.fid, normaliseForm(error.response.data))
			);
			yield put(statusReady(action.fid));
		} else {
			yield put(statusError(action.fid, getErrorMessageOrDefault(error)));
		}
	}
}

export function* saveAndClose(action: Actions) {
	yield whenReady(action.fid);
	yield put(statusLoading(action.fid));

	try {
		yield call(forms.save, action.fid, {
			handleErrors: true,
		});

		yield call(forms.close, action.fid, {
			handleErrors: true,
		});

		yield put(statusClosed(action.fid));
	} catch (error) {
		const e = error as AxiosError;
		if (e.response) {
			const resp = e.response as AxiosResponse;

			// 400 errors = expected validation errors
			// Errors should be in the form instance so we can
			// just merge the update in as normal
			if (resp.status === 400) {
				yield put(mergeUpdate(action.fid, normaliseForm(resp.data)));
				yield put(statusReady(action.fid));
			} else {
				yield put(statusError(action.fid));
			}
		}
	}
}

export function* close({ fid }: Actions) {
	const status: FormStatusTypes = yield select(getStatus, { fid });

	// Forms that are either ready or have errors can be closed.
	while (status !== "READY" && status !== "ERROR") {
		const action: UpdateFormStatus = yield take(actions.FORM_STATUS_UPDATE);
		if (
			action.fid === fid &&
			(action.status === "READY" || action.status === "ERROR")
		)
			break;
	}

	yield put(statusLoading(fid));

	try {
		yield call(forms.close, fid, { handleErrors: true });
	} catch (err) {
		// Console log the error, but we don't really need to inform the user here
		console.warn(`There was an error closing the form instance: ${fid}`);
	} finally {
		yield put(statusClosed(fid));
	}
}

// Init
export function* init() {
	// Listen for INIT_APP action
	yield take(INIT_APP);

	try {
		// Check if the user has a valid token
		const auth: boolean = yield call(tokens.validate);

		// If the user is authenticated
		if (auth) {
			yield put(authenticated());
		} else {
			// Get login provider
			// TODO: I think this should be pulled out by the API, not here...
			const tenant = window.location.host.substr(
				0,
				window.location.host.indexOf(".")
			);

			const authProvider: AxiosResponse = yield call(
				tenants.getAuthType,
				tenant
			);

			yield put(
				setAuthType(
					authProvider.data.data.provider,
					tenant,
					authProvider.data.data.clientId
				)
			);
		}
	} catch (error) {
		const e = error as AxiosError;
		// Catch errors where the user has entered an invalid tenant in the url
		// and redirect to the generic 404 page
		if (e.response && e.response.status === 404) {
			history.push("/404");
		}
		// The only other errors here would be a result of the tenant not being
		// configured correctly
		else {
			console.error(e);
		}
	}

	// Set the app the ready
	yield put(appReady());
}

// Handle auth
export function* auth() {
	while (true) {
		yield take(AUTHENTICATED);
		yield take(LOGOUT);
		yield call(logout);
	}
}

export function* logout() {
	try {
		yield call(tokens.revoke);
	} catch (error) {
		console.error("Error revoking auth token", error);
	} finally {
		window.location.href = "/";
	}
}

function* mySaga() {
	yield all([init(), auth(), watchFormChanges()]);
}

export default mySaga;
